import UpgradeV2Image from '@/images/agents/UpgradeV2Edit.jpg';
import { useCloneAgent, useRevertAgent } from '@/pages/agents/api';
import { useAnalytics } from '@/services/analytics';
import { AppRoutes } from '@/utils/types';
import { AgentConvertStatus, IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { atom, useRecoilState } from 'recoil';
import { MainText, Row, StyledButton, StyledDialog as StyledDialogTemplate, Text, TextContainer } from './common';
import { AGENT_MODAL_Z_INDEXES } from './constants/zIndex';

export const isUpgradeV2ModalDraftOpen = atom({
    key: 'is-upgrade-v2-modal-draft-open',
    default: false,
});

const StyledDialog = styled(StyledDialogTemplate)`
    z-index: ${AGENT_MODAL_Z_INDEXES.UPGRADE_V2_DRAFT};
`;

interface Props {
    agent: IAgent;
}

export function UpgradeV2ModalDraft({ agent }: Props) {
    const [isOpen, setOpen] = useRecoilState(isUpgradeV2ModalDraftOpen);
    const { mutateAsync: migrate } = useCloneAgent();
    const { mutate: revertAgent } = useRevertAgent();

    const mixpanel = useAnalytics();
    const navigate = useNavigate();

    useEffect(() => {
        return () => {
            setOpen(false);
        };
    }, []);

    useEffect(() => {
        setOpen(agent.conversion === AgentConvertStatus.V2Convertable);
    }, [agent.conversion]);

    useEffect(() => {
        mixpanel?.track('agents-upgrade-v2', { event: 'view', from: 'upgrade-modal-draft' });
    }, []);

    const handleClose = () => {
        setOpen(false);
    };

    const onSubmit = async () => {
        if (agent.id) {
            mixpanel?.track('agents-upgrade-v2', { event: 'click', from: 'upgrade-modal-draft' });

            await migrate(agent.id);
            await revertAgent();

            navigate(AppRoutes.Agent);
            handleClose();
        }
    };

    return (
        <StyledDialog open={isOpen} onClose={handleClose}>
            <img src={UpgradeV2Image} />
            <TextContainer>
                <Text>Upgrade to Agents 2.0</Text>
                <MainText>
                    Try the Agent 2.0 experience with enhanced customization, improved conversational flow, and new display options. Click
                    "Let's go" and we'll create a new and improved version of your agent for you.
                </MainText>
            </TextContainer>
            <Row>
                <StyledButton onClick={handleClose} variant="contained">
                    Dismiss
                </StyledButton>
                <StyledButton onClick={onSubmit} variant="contained" main>
                    Let’s go
                </StyledButton>
            </Row>
        </StyledDialog>
    );
}
